import React, { forwardRef } from 'react';
import { ICoreContext, IGridSettings, IViewPort } from '@msdyn365-commerce/core-internal';
import useEmblaCarousel from 'embla-carousel-react';
import { useImageSettings } from '../../hooks/useImageSettings';
import classnames from 'classnames';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ImageWithLoading } from '../image-with-loading';
import { RecommendedProductsCarouselItemSkeleton } from './loading-state';
import { MappedProduct } from '../../../integrations/relewise/types/mapped';
import { ProductLabels } from '../product-labels';
import { MemberPriceTag } from '../member-price-tag';

const IMAGE_VIEWPORT_SETTINGS = {
    xs: { w: 285, h: 380 },
    sm: { w: 285, h: 380 },
    md: { w: 285, h: 380 },
    lg: { w: 285, h: 380 },
    xl: { w: 285, h: 380 }
};

interface ProductRecommendationCarouselItemProps {
    product: MappedProduct;
    imageSettings: IViewPort;
    gridSettings: IGridSettings | undefined;
    ctaLabel: string;
    maxProductLabels: number | undefined;
}
const RecommendedProductsCarouselItem = ({
    product,
    imageSettings,
    gridSettings,
    ctaLabel,
    maxProductLabels
}: ProductRecommendationCarouselItemProps) => {
    return (
        <a className='recommended-product-card' href={product.productPageUrl}>
            <ImageWithLoading
                wrapperClassname='recommended-product-card__image-wrapper'
                src={product.imgUrl}
                alt={product.displayName}
                title={product.displayName}
                imageSettings={{
                    viewports: imageSettings
                }}
                gridSettings={gridSettings ?? {}}
                loadFailureBehavior='empty'
                showEmptyImageThumbnail
            />
            <ProductLabels labels={product.labels} maxProductLabels={maxProductLabels} />

            <div className='recommended-product-card__info'>
                <div className='recommended-product-card__title'>{product.displayName}</div>
                <div className='recommended-product-card__price'>
                    {!!product.memberPriceData ? (
                        <>
                            <MemberPriceTag memberPrice={product.memberPriceData} className='recommended-product-card__final-price' />
                            <span className={'recommended-product-card__final-price'}>{product.basePriceFormatted}</span>
                        </>
                    ) : (
                        <>
                            <span
                                className={classnames('recommended-product-card__final-price', {
                                    'recommended-product-card__final-price--discounted': product.hasDiscount
                                })}
                            >
                                {product.finalPriceFormatted}
                            </span>
                            {product.hasDiscount && (
                                <span className='recommended-product-card__original-price'>{product.basePriceFormatted}</span>
                            )}
                        </>
                    )}
                </div>
                <div className='recommended-product-card__cta'>
                    <span className='recommended-product-card__cta-text'>{ctaLabel}</span>
                    <span className='recommended-product-card__cta-icon'>→</span>
                </div>
            </div>
        </a>
    );
};

interface ProductRecommendationCarouselProps {
    products: MappedProduct[];
    title: string;
    context: ICoreContext;
    ctaLabel: string;
    isLoading: boolean;
}
export const RecommendedProductsCarousel = forwardRef<HTMLElement, ProductRecommendationCarouselProps>(
    ({ products, title, context, ctaLabel, isLoading }, ref) => {
        const imageSettings = useImageSettings(IMAGE_VIEWPORT_SETTINGS);
        const isMobile = useIsMobile(context);
        const {
            request: { gridSettings }
        } = context;

        const [emblaRef, emblaApi] = useEmblaCarousel({
            dragFree: true
        });
        const scrollPrev = () => emblaApi?.scrollPrev();
        const scrollNext = () => emblaApi?.scrollNext();

        // Fallback: hide if no products where loaded
        if (!isLoading && !products?.length) {
            return <></>;
        }

        return (
            <section
                ref={ref}
                className={classnames('recommended-products', {
                    'recommended-products--mobile': isMobile
                })}
            >
                <h2 className='recommended-products__title'>{title}</h2>

                <div className='recommended-products__carousel'>
                    <button
                        className='recommended-products__carousel-arrow recommended-products__carousel-arrow--left'
                        onClick={scrollPrev}
                    >
                        ‹
                    </button>

                    <div className='recommended-products__carousel-viewport' ref={emblaRef}>
                        <ul className='recommended-products__carousel-items'>
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                      <li className='recommended-products__carousel-slide' key={i}>
                                          <RecommendedProductsCarouselItemSkeleton />
                                      </li>
                                  ))
                                : products.map(product => (
                                      <li className='recommended-products__carousel-slide' key={product.relewiseVariantId}>
                                          <RecommendedProductsCarouselItem
                                              product={product}
                                              imageSettings={imageSettings}
                                              gridSettings={gridSettings}
                                              ctaLabel={ctaLabel}
                                              maxProductLabels={context.request.app?.config.maxProductLabels}
                                          />
                                      </li>
                                  ))}
                        </ul>
                    </div>

                    <button
                        className='recommended-products__carousel-arrow recommended-products__carousel-arrow--right'
                        onClick={scrollNext}
                    >
                        ›
                    </button>
                </div>
            </section>
        );
    }
);
